import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useAuth } from '../../contexts/AuthContext';
import usePatientDoc from '../../hooks/usePatientDoc';
import { useForm } from '@tanstack/react-form';
import { PatientDoc } from '../../types/Patient/PatientDoc';
import { useUpdateUserDoc } from '../../hooks/useUpdateUserDoc';
import { Timestamp } from 'firebase/firestore';
import { getCampaignInfo } from '../../data/campaigns/getCampaignInfo';
import { EnrollmentCampaign } from '../../types/Campaigns/EnrollmentCampaign';
import { Button } from '../../components';
import * as Sentry from '@sentry/browser';

export const Route = createFileRoute('/_authenticated/welcome')({
  component: Welcome,
});

function Welcome() {
  const [partnerLogoUrl, setPartnerLogoUrl] = React.useState<string>('');
  const { currentUser } = useAuth();
  const patientId = currentUser?.uid || '';
  const patientDocQuery = usePatientDoc(patientId);
  const patientDocData = patientDocQuery.data as PatientDoc;
  const campaignId = patientDocData?.enrollmentInfo?.campaignId;
  const navigate = useNavigate();

  const campaignInfoQuery = useQuery({
    enabled: !!campaignId,
    queryKey: ['campaignInfo', campaignId],
    queryFn: async () => {
      let data: EnrollmentCampaign | undefined = undefined;
      if (patientDocData?.enrollmentInfo?.campaignId && patientDocData.enrollmentInfo.campaignId !== 'unset') {
        data = await getCampaignInfo(patientDocData.enrollmentInfo.campaignId);
      }
      return data;
    },
  });

  React.useEffect(() => {
    if (campaignInfoQuery && campaignInfoQuery.data) {
      setPartnerLogoUrl(campaignInfoQuery.data.companyLogoUrl);
    }
  }, [campaignInfoQuery]);

  const { mutate } = useUpdateUserDoc();

  const handleChoiceChange = (choice: 'self' | 'guided') => {
    mutate(
      {
        campaignId: patientDocData?.enrollmentInfo?.campaignId ? patientDocData.enrollmentInfo.campaignId : 'unset',
        patientId: patientId,
        choice: choice,
        enrollmentEmailSent: patientDocData?.enrollmentInfo?.enrollmentEmailSent
          ? patientDocData.enrollmentInfo.enrollmentEmailSent
          : Timestamp.now(),
        choiceMade: Timestamp.now(),
      },
      {
        onSuccess: () => {
          // alert("Successfully updated document");
        },
        onError: (error) => {
          Sentry.captureException(error); // CAPTURE THE ERROR
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  const handleNavToHome = () => {
    navigate({ to: `/home` });
  };

  const form = useForm({
    defaultValues: {
      campaignId: patientDocData?.enrollmentInfo?.campaignId ? patientDocData.enrollmentInfo.campaignId : 'unset',
      patientId: patientId,
      choice: patientDocData?.enrollmentInfo?.choice ? patientDocData.enrollmentInfo.choice : 'unset',
      enrollmentEmailSent: patientDocData?.enrollmentInfo?.enrollmentEmailSent,
    },
  });

  if (patientDocData && currentUser) {
    return (
      <div className="">
        <div className="flex h-full max-w-[1000px] flex-col items-center">
          <div className="flex flex-row items-center justify-around">
            {partnerLogoUrl && (
              <div className="h-9/12 w-5/12">
                <img src={partnerLogoUrl} alt="partner_logo" />
              </div>
            )}
            {partnerLogoUrl && (
              <div className="w-1/12 place-items-center">
                {/* <Activity className="text-black" height="3.5em" width="3.5em" /> */}
              </div>
            )}
            <div className="w-5/12">
              <img src="https://storage.googleapis.com/mideoid-console-public/logos/MIDEOhealth.png" alt="mideo_logo" />
            </div>
          </div>
          {/* TODO: Add captions track and disable warning */}
          {/* eslint-disable-next-line */}
          <video id="patient-education-video" className="my-4 h-4/6 w-1/3" controls>
            <source
              src="https://storage.googleapis.com/mideoid-console-public/videos/instructional/Self%20guided%20vs%20clinician%20guided%20choice%20video.mp4"
              type="video/mp4"
            />
          </video>
          <div id="path-selection-form">
            <p className="text-bold text-3xl">Choose the process below that is right for you.</p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                form.handleSubmit();
              }}
            >
              <div className="my-4">
                <form.Field
                  name="choice"
                  children={(field) => (
                    <input
                      type="radio"
                      id="self"
                      name="choice"
                      value="self"
                      checked={field.state.value === 'self'}
                      onChange={() => {
                        field.setValue('self');
                        handleChoiceChange('self');
                      }}
                    />
                  )}
                />
                <label htmlFor="self" className="ml-2 text-2xl">
                  Self-Guided
                </label>
                <a
                  target="_blank"
                  rel="noreferrer"
                  href="http://mideohealth.com/for-patients/"
                  // className="rounded-lg bg-sky-700 px-5 py-2.5 text-sm font-medium text-white hover:bg-sky-800 focus:outline-hidden focus:ring-4 focus:ring-blue-300 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
                  className="m-2 rounded-md bg-sky-700 px-4 py-2 text-white hover:bg-sky-800 disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-500 disabled:shadow-none sm:px-8 sm:py-3"
                >
                  Learn More
                </a>
                {patientDocData?.enrollmentInfo?.choice === 'self' && (
                  <div className="my-4 flex flex-col items-center">
                    <Button
                      id="nav-to-self-guided-home"
                      type="button"
                      onClick={handleNavToHome}
                      textContent="Get Started"
                    />
                  </div>
                )}
              </div>
              <div className="my-6">
                <form.Field
                  name="choice"
                  children={(field) => (
                    <input
                      type="radio"
                      id="guided"
                      name="choice"
                      value="guided"
                      checked={field.state.value === 'guided'}
                      onChange={() => {
                        field.setValue('guided');
                        handleChoiceChange('guided');
                      }}
                    />
                  )}
                />
                <label htmlFor="guided" className="ml-2 text-2xl">
                  Clinician-Guided
                </label>
                <a
                  target="_blank"
                  rel="noreferrer"
                  href="http://mideohealth.com/for-patients/"
                  // className="rounded-lg bg-sky-700 px-5 py-2.5 text-sm font-medium text-white hover:bg-sky-800 focus:outline-hidden focus:ring-4 focus:ring-blue-300 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
                  className="m-2 rounded-md bg-sky-700 px-4 py-2 text-white hover:bg-sky-800 disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-500 disabled:shadow-none sm:px-8 sm:py-3"
                >
                  Learn More
                </a>
                {patientDocData?.enrollmentInfo?.choice === 'guided' && (
                  <div id="schedule-intake" className="my-4 flex flex-col place-content-center">
                    <a
                      target="_blank"
                      rel="noreferrer"
                      href="https://www.signupgenius.com/go/70A084DAAA92DA6FC1-52860407-mideo#/"
                      // className="rounded-lg bg-sky-700 px-5 py-2.5 text-sm font-medium text-white hover:bg-sky-800 focus:outline-hidden focus:ring-4 focus:ring-blue-300 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
                      className="m-2 rounded-md bg-sky-700 px-4 py-2 text-center text-white hover:bg-sky-800 disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-500 disabled:shadow-none sm:px-8 sm:py-3"
                    >
                      Schedule Intake
                    </a>
                  </div>
                )}
              </div>
            </form>
          </div>
          <div className="h-32"></div>
        </div>
      </div>
    );
  }
  return <div>Loading...</div>;
}
